// ProductTableRow.js
import React from "react";
import { useMemo } from "react";
import RowStatusIndicator from "../../RowStatusIndicator/RowStatusIndicator";
import PopupChecklist from "../../PopupChecklist/PopupChecklist";
import isEqual from "lodash.isequal";
import './ProductTableRow.css';
import EditTablePopup from "../EditTablePopup/EditTablePopup";


const ProductTableRow = ({
  product,
  fields,
  dropdownData,
  selectedProducts,
  handleSelect,
  handleCellClick,
  editingCell,
  editedValue,
  setEditedValue,
  setEditingCell,
  pendingChanges,
  setPendingChanges,
  user,
  status,
  canEditRow,
  FileUploadButton,
  NumberPartsEditor,
  renderField,
  handleContextMenu,
  currentProducts,
  getDisplayLabel,
  updatePendingChanges,
  handleDoubleClick,
}) => {
  const edittableFields = useMemo(() => {
    return Array.isArray(user?.fieldsAccess)
      ? fields
          .map((f) => f.fields?.toString())
          .filter((field) => user.fieldsAccess.includes(field))
      : [];
  }, [fields, user?.fieldsAccess]);

  const isRowEditable = canEditRow(product, user);

  return (
    <tr
      className="advanceProduct-table-row"
      onContextMenu={(e) => {
        e.preventDefault();
        handleContextMenu && handleContextMenu(e, product);
      }}
    >
      <td className="row-checkbox-cell">
        <input
          type="checkbox"
          className="row-checkbox"
          checked={selectedProducts.includes(product._id)}
          onChange={() => handleSelect(product._id)}
        />
      </td>
      <td className="row-status-cell">
        <RowStatusIndicator product={product} status={status} />
      </td>

      {fields.map((field, index) => {
        const value =
          pendingChanges?.[product._id]?.[field.fields] ??
          product[field.fields];
        const isFileField = field.type === "file";
        const editable =
          edittableFields.includes(field.fields) && isRowEditable;

        return (
          <td
            key={`${product._id}-${field.fields}-${index}`}
            className={`table-cell field-${field.fields} ${
              !value ? "empty-cell" : ""
            }`}
            onDoubleClick={() =>
              handleDoubleClick(
                field,
                value,
                isRowEditable,
                edittableFields,
                product._id,
                handleCellClick
              )
            }
          >
            {/* ========== EDIT MODE ========== */}
            {editingCell?.rowId === product._id &&
            editingCell?.fieldName === field.fields ? (
              field.api && field.multiple ? (
                <>
                  <div
                    className="multi-select-display"
                    onClick={() =>
                      setEditingCell({
                        rowId: product._id,
                        fieldName: field.fields,
                        showPopup: true,
                      })
                    }
                  >
                    {Array.isArray(value) && value.length > 0 ? (
                      value.map((val, i) => {
                        const label = getDisplayLabel(
                          val,
                          field.fields,
                          dropdownData
                        );
                        return (
                          <span
                            key={val?._id || label || i}
                            className="multi-chip"
                          >
                            {label}
                          </span>
                        );
                      })
                    ) : (
                      <span className="placeholder">No users</span>
                    )}
                  </div>

{editingCell?.showPopup &&
  (field.editPopUp === "true" && field.popupCalling ? (
    Array.isArray(product[field.fields]) &&
    product[field.fields].length > 1 &&
    !editingCell.popupEntryId ? (
      <div className="popup-select-multiple">
        <p>Select an entry to edit:</p>
        {product[field.fields].map((entryId) => (
          <button
            key={entryId}
            onClick={() =>
              setEditingCell((prev) => ({
                ...prev,
                popupEntryId: entryId,
              }))
            }
            className="popup-select-btn"
          >
            {entryId}
          </button>
        ))}
      </div>
    ) : (
      <EditTablePopup
        id={
          editingCell?.popupEntryId ||
          (Array.isArray(product[field.fields])
            ? product[field.fields][0]
            : product[field.fields])
        }
        api={field.api}
        fields={field.popupCalling}
        onSuccess={() => {
          setEditingCell(null);
        }}
      />
    )
  ) : (
    <PopupChecklist
      options={dropdownData[field.fields] || []}
      selectedValues={
        Array.isArray(value)
          ? value
              .filter(Boolean)
              .map((val) => (typeof val === "object" ? val._id : val))
          : []
      }
      onChange={(updatedValues) => {
        const currentValues = Array.isArray(value)
          ? value
              .filter(Boolean)
              .map((val) => (typeof val === "object" ? val._id : val))
          : [];

        if (!isEqual(currentValues, updatedValues)) {
          updatePendingChanges(
            setPendingChanges,
            product._id,
            field.fields,
            updatedValues
          );
        }
        setEditingCell(null);
      }}
      onClose={() => setEditingCell(null)}
    />
  ))}

                </>
              ) : isFileField ? (
                <div className="file-field-container">
                  <div className="file-actions">
                    <button
                      onClick={() => value && window.open(value, "_blank")}
                      className="view-file-button"
                      title="View file"
                      disabled={!value}
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    <button
                      onClick={() =>
                        value && navigator.clipboard.writeText(value)
                      }
                      className="copy-link-button"
                      title="Copy link"
                      disabled={!value}
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                    <FileUploadButton
                      fieldName={field.fields}
                      productId={product._id}
                      onUploadSuccess={(id, fieldName, link) => {
                        updatePendingChanges(
                          setPendingChanges,
                          id,
                          fieldName,
                          link
                        );
                      }}
                    />
                  </div>
                </div>
              ) : field.type === "numberparts" ? (
                <NumberPartsEditor
                  product={product}
                  field={field}
                  editedValue={editedValue}
                  setEditedValue={setEditedValue}
                  setPendingChanges={setPendingChanges}
                  setEditingCell={setEditingCell}
                  currentProducts={currentProducts}
                />
              ) : (
                <input
                  type={field.type === "date" ? "date" : "text"}
                  value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                  onBlur={() => {
                    const trimmed =
                      typeof editedValue === "string"
                        ? editedValue.trim()
                        : editedValue;
                    if (!isEqual(value, trimmed)) {
                      updatePendingChanges(
                        setPendingChanges,
                        product._id,
                        field.fields,
                        trimmed
                      );
                    }
                    setEditingCell(null);
                  }}
                  autoFocus
                  className="editable-input"
                />
              )
            ) : (
              // ========== READ MODE ==========
              (() => {
                const raw = product[field.fields];
                let displayText = "";

                if (field.fields === "photo") {
                  return (
                    <img
                      src={value}
                      alt={product.title}
                      className="photo-thumbnail"
                    />
                  );
                }

                if (field.api) {
                  displayText = Array.isArray(raw)
                    ? raw
                        .map((item) =>
                          typeof item === "object"
                            ? item?.name || item?.userId || "Unnamed"
                            : dropdownData[field.fields]?.find(
                                (opt) => opt.id === item
                              )?.label || item
                        )
                        .join(", ")
                    : typeof raw === "object"
                    ? raw?.name || raw?.userId || "Unnamed"
                    : raw || "";
                } else if (field.type === "date" && value) {
                  displayText = new Date(value).toLocaleDateString("en-GB");
                } else {
                  displayText =
                    typeof value === "string" ? value : String(value ?? "");
                }

                const maxLength = 80;
                const isExpanded =
                  editingCell?.rowId === product._id &&
                  editingCell?.fieldName === field.fields;

                return displayText.length > maxLength ? (
                  <div className="expandable-text">
                    {isExpanded
                      ? displayText
                      : `${displayText.slice(0, maxLength)}...`}
                    <span
                      className="read-more-btn"
                      onClick={() =>
                        setEditingCell(
                          isExpanded
                            ? null
                            : {
                                rowId: product._id,
                                fieldName: field.fields,
                              }
                        )
                      }
                    >
                      {isExpanded ? " Read Less" : " Read More"}
                    </span>
                  </div>
                ) : (
                  displayText ||
                    renderField(displayText, field.fields, product._id)
                );
              })()
            )}
          </td>
        );
      })}
    </tr>
  );
};

export default ProductTableRow;
